import React, {FC} from 'react';
import {getLocale} from '@epic-core/common/dist';
import {Datapoint} from '@epic-visualizations/charts';

import useLocalization from '../../../../../../hooks/useLocalization';
import useWidth from './useWidth';
import PerformanceTooltip from './PerformanceTooltip';
import {ChartSkeleton} from '../../../../../common/skeleton';

import {Trends} from './types';
import {Formatter, Releases} from '../../../../../../types';

import {PerformanceChartContainer, StyledLineChart} from './styles';

export interface ChartProps {
    data: Datapoint[];
    height?: number;
    tooltipLabelFormatter?: Formatter<string | number>;
    xTickFormatter?: Formatter<string | number>;
    yTickFormatter?: Formatter<string | number>;
    tooltipFormatter?: any;
    xAxis?: string;
    trends?: Trends;
    releases?: Releases;
    loading: boolean;
    yAxisProps?: object;
    xAxisProps?: object;
    referenceLineProps?: object;
}

const defaultFormatter: Formatter<undefined> = () => undefined;

const PerformanceChart: FC<ChartProps> = ({
    data: propsData,
    height,
    tooltipLabelFormatter = defaultFormatter,
    xTickFormatter = defaultFormatter,
    yTickFormatter = defaultFormatter,
    tooltipFormatter = defaultFormatter,
    trends,
    releases,
    loading,
    ...props
}: ChartProps) => {
    const [containerRef] = useWidth();
    const getMessage = useLocalization();

    const tooltipProps = {
        isAnimationActive: true,
        allowEscapeViewBox: {x: false, y: false}
    };

    if (loading) {
        return <ChartSkeleton pb={2} pt={4} height={520} width="100%" hasLegend={false} />;
    }

    const locale = getLocale();

    return (
        <PerformanceChartContainer ref={containerRef}>
            <StyledLineChart
                responsive
                height={height}
                data={propsData}
                locales={locale}
                showLegend={false}
                xTickFormatter={xTickFormatter(locale)}
                yTickFormatter={yTickFormatter(locale)}
                tooltipLabelFormatter={tooltipLabelFormatter(locale)}
                margin={{top: 5, right: 0, bottom: 15, left: 0}}
                tooltipProps={tooltipProps}
                tooltipContent={PerformanceTooltip({
                    trends,
                    releases,
                    tooltipFormatter: tooltipFormatter(locale),
                    getMessage
                })}
                {...props}
            />
        </PerformanceChartContainer>
    );
};

export default PerformanceChart;
